import React, { useCallback, useMemo, useState } from 'react';
import SideBar, { SideBarItem } from '../sidebar/Sidebar';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { POLICIES } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import NavBar from '../navbar/NavBar';
import BottomTabs from '../bottomTabs/BottomTabs';
import { faLandmark, faMicrochip, faSimCard, faSignal, faCopyright, faIdCard, faCar, faMapLocationDot, faUserShield, faKey, faTag, faTags, faTrailer, faWarning, faBell, faStopCircle, faClipboardCheck, faList, faFileImport, faSatelliteDish } from '@fortawesome/free-solid-svg-icons';
interface Props {
    children: React.ReactNode
}

const AuthenticatedLayout: React.FunctionComponent<Props> = ({ children }: Props) => {
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);

    const isPlatformAdmin = UsersService.isPlatformAdmin(loggedUser?.roles);

    const validateItem = useCallback((item: SideBarItem) => {
        const type = item.type ?? 'AND';
        const policies = item.policies ?? [];
        const userPolicies = loggedUser?.policies ?? [];
        return item.policies.length > 0 ? UsersService.hasPolicies(userPolicies, policies, type) : true;
    }, [loggedUser]);

    const sidebarItems: SideBarItem[] = useMemo(() => {
        const items: SideBarItem[] = [
            {
                text: t('menu.device_checklist'),
                url: '/device-checklist',
                icon: faClipboardCheck,
                policies: [
                    'MY_VEHICLES_READ'
                ],
                type: 'OR'
            },
            {
                text: t('menu.my_vehicles'),
                url: '/vehicles',
                icon: faCar,
                policies: [
                    'MY_VEHICLES_READ', 'MY_VEHICLES_WRITE'
                ],
                type: 'OR'
            },
            {
                text: t('menu.my_devices'),
                url: '/devices',
                icon: faMicrochip,
                policies: [
                    'MY_DEVICES_READ'
                ],
                type: 'OR'
            },
            {
                text: t('menu.sim_cards'),
                url: '/simcards',
                icon: faSimCard,
                policies: [
                    'MY_SIMCARDS_READ'
                ],
                type: 'OR'
            },
            {
                text: t('menu.identification_tags'),
                url: '/identificationtags',
                icon: faTag,
                policies: [
                    'MY_IDENTIFICATION_TAGS_READ',
                    'MY_IDENTIFICATION_TAGS_WRITE',
                ],
                type: 'OR'
            },
            {
                text: t('menu.my_tags'),
                url: '/tags',
                icon: faTags,
                policies: [
                    'MY_TAGS_READ'
                ],
                type: 'OR'
            },
            {
                text: t('menu.drivers'),
                url: '/drivers',
                icon: faIdCard,
                policies: [
                    'DRIVERS_READ', 'DRIVERS_WRITE'
                ],
                type: 'OR'
            },
            {
                text: t('menu.tracking_items'),
                url: '/tracking-items',
                icon: faList,
                policies: [
                    'MY_VEHICLES_READ'
                ],
                type: 'OR'
            },
            {
                text: t('menu.my_trailers'),
                url: '/trailers',
                icon: faTrailer,
                policies: [
                    'MY_TRAILERS_READ', 'MY_TRAILERS_WRITE'
                ],
                type: 'OR'
            },
            {
                text: t('menu.api_tokens'),
                url: '/apitokens',
                icon: faKey,
                policies: [
                    'API_TOKENS_READ', 'API_TOKENS_WRITE'
                ],
                type: 'OR'
            },
            {
                text: t('menu.alarm_settings'),
                url: '/alarmsettings',
                icon: faBell,
                policies: [
                    'ALARM_SETTINGS_READ', 'ALARM_SETTINGS_WRITE'
                ],
                type: 'OR'
            },
            {
                text: t('menu.location'),
                url: '/location',
                icon: faMapLocationDot,
                policies: [
                    'LOCATION_READ'
                ],
                type: 'OR'
            },

            {
                text: t('menu.stopping_areas'),
                url: '/stoppingareas',
                icon: faStopCircle,
                policies: [
                    'STOPPING_AREAS_READ', 'STOPPING_AREAS_WRITE'
                ],
                type: 'OR'
            },
            {
                text: t('menu.security'),
                url: '/security',
                icon: faUserShield,
                policies: [
                    'USERS_READ',
                    'USERS_WRITE',
                    'ROLES_READ',
                    'ROLES_WRITE',
                    'RULES_READ',
                    'RULES_WRITE'
                ],
                type: 'OR'
            },
            {
                text: t('menu.configuration'),
                url: '/#',
                policies: [
                    'COMPANIES_READ',
                    'COMPANIES_WRITE',
                    'CONFIG_DEVICES_READ',
                    'CONFIG_DEVICES_WRITE',
                    'CONFIG_TAGS_READ',
                    'CONFIG_TAGS_WRITE',
                    'CONFIG_SIMCARDS_READ',
                    'CONFIG_SIMCARDS_WRITE',
                    'CONFIG_PLANS_READ',
                    'CONFIG_PLANS_WRITE',
                    'CONFIG_DEVICE_BRAND_WRITE',
                    'CONFIG_DEVICE_BRAND_READ',
                    'CONFIG_DEVICE_MODEL_WRITE',
                    'CONFIG_DEVICE_MODEL_READ',
                    'CONFIG_IDENTIFICATION_TAGS_READ',
                    'CONFIG_BULK_IMPORT_READ',
                    'CONFIG_BULK_IMPORT_WRITE',
                ],
                type: 'OR',
                items: [
                ...(isPlatformAdmin
                    ? [{
                        text: t('menu.device_checklist'),
                        url: '/device-checklist',
                        icon: faClipboardCheck,
                        policies: [] as (keyof typeof POLICIES)[],
                        items: []
                    },
                    {
                        text: t('menu.tracking_items'),
                        url: '/tracking-items',
                        icon: faList,
                        policies: [] as (keyof typeof POLICIES)[],
                        items: []
                    }]
                    : []),
                {
                    text: t('menu.companies'),
                    url: '/companies',
                    icon: faLandmark,
                    policies: [
                        'COMPANIES_READ', 'COMPANIES_WRITE'
                    ],
                    type: 'OR',
                    items: []
                },
                {
                    text: t('menu.devices'),
                    url: '/devices',
                    icon: faMicrochip,
                    policies: [
                        'CONFIG_DEVICES_READ', 'CONFIG_DEVICES_WRITE'
                    ],
                    type: 'OR',
                    items: []
                },
                {
                    text: t('menu.tags'),
                    url: '/tags',
                    icon: faTags,
                    policies: [
                        'CONFIG_TAGS_READ', 'CONFIG_TAGS_WRITE'
                    ],
                    type: 'OR',
                    items: []
                },
                {
                    text: t('menu.sim_cards'),
                    url: '/simcards',
                    icon: faSimCard,
                    policies: [
                        'CONFIG_SIMCARDS_READ', 'CONFIG_SIMCARDS_WRITE'
                    ],
                    type: 'OR',
                    items: []
                },
                {
                    text: t('menu.identification_tags'),
                    url: '/identificationtags',
                    icon: faTag,
                    policies: [
                        'CONFIG_IDENTIFICATION_TAGS_READ', 'CONFIG_IDENTIFICATION_TAGS_WRITE'
                    ],
                    type: 'OR',
                    items: []
                },
                {
                    text: t('menu.plans'),
                    url: '/plans',
                    icon: faSignal,
                    policies: [
                        'CONFIG_PLANS_READ', 'CONFIG_PLANS_WRITE'
                    ],
                    type: 'OR',
                    items: []
                },
                {
                    text: t('menu.devices_brands'),
                    url: '/config-devices',
                    icon: faCopyright,
                    policies: [
                        'CONFIG_DEVICE_BRAND_WRITE', 'CONFIG_DEVICE_BRAND_READ', 'CONFIG_DEVICE_MODEL_WRITE', 'CONFIG_DEVICE_MODEL_READ'
                    ],
                    type: 'OR',
                    items: []
                },
                {
                    text: t('menu.bulk_import'),
                    url: '/bulkimport',
                    icon: faFileImport,
                    policies: [
                        'CONFIG_BULK_IMPORT_READ', 'CONFIG_BULK_IMPORT_WRITE'
                    ],
                    type: 'OR',
                    items: []
                },
                ...(isPlatformAdmin
                    ? [{
                        text: t('menu.fota'),
                        url: '/fota',
                        icon: faSatelliteDish,
                        policies: [] as (keyof typeof POLICIES)[],
                        items: []
                    }]
                    : [])],
            }
        ];
        const filtered = items
            .filter(item => validateItem(item))
            .map(item => {
                item.items = item && item.items ? item.items.filter(i => validateItem(i)) : undefined;
                return item;
            });

        // Dedupe: top-level leaf items take priority. If a sub-item inside
        // a group shares its URL with a visible top-level leaf, drop the
        // sub-item so the entry only appears once (at the top level).
        const topLevelLeafUrls = new Set<string>();
        for (const item of filtered) {
            const isLeaf = !item.items || item.items.length === 0;
            if (isLeaf && item.url && item.url !== '/#') {
                topLevelLeafUrls.add(item.url);
            }
        }
        const result = filtered
            .map(item => {
                if (item.items && item.items.length > 0) {
                    item.items = item.items.filter(sub => !(sub.url && topLevelLeafUrls.has(sub.url)));
                }
                return item;
            })
            // Drop group items (those originally declared with sub-items) that
            // no longer have any visible sub-items after policy + dedupe filtering.
            .filter(item => {
                const wasGroup = item.url === '/#';
                if (!wasGroup) return true;
                return !!(item.items && item.items.length > 0);
            });

        // If only one group remains visible, flatten it: show its sub-items at
        // the top level instead of nesting them under a single collapsible section.
        if (result.length === 1 && result[0].items && result[0].items.length > 0) {
            return result[0].items;
        }
        return result;
    }, [loggedUser, validateItem]);

    const [sidebarOpen, setSidebarOpen] = useState(false);

    return (
        <>
            <div className="main-wrapper">
                <NavBar onToggleSidebar={() => setSidebarOpen(prev => !prev)} />
                <SideBar items={sidebarItems} key="sidebar" mobileOpen={sidebarOpen} onMobileClose={() => setSidebarOpen(false)} />
                <main className="main-container container-fluid">
                    {children}
                </main>
            </div>
            <BottomTabs onMoreClick={() => setSidebarOpen(true)} />
        </>
    );
};

export default AuthenticatedLayout;
